import { Box, Group, Select, Text, Loader } from '@mantine/core'
import { IconWorld } from '@tabler/icons'
import Image from 'next/image'
import PropTypes from 'prop-types'
import React, { forwardRef } from 'react'
import { useLocaleContext } from '../../context/locale'
import countries from '../../utils/countries.json'

// Solo 5 países estáticos (pre-generados en build time)
export const hitsCountries = [
  'US', // United States
  'AR', // Argentina
  'BR', // Brazil
  'MX', // Mexico
  'AU' // Australia
]

// Todos los países disponibles con información
const availableCountries = [
  'US',
  'AR',
  'BR',
  'MX',
  'AU',
  'IN',
  'BE',
  'PH',
  'SE',
  'DK',
  'NO',
  'ZA',
  'NL',
  'AE',
  'AT',
  'BD',
  'BG',
  'CH',
  'CR',
  'CZ',
  'EC',
  'EG',
  'FI',
  'GR',
  'GT',
  'HK',
  'HN',
  'HR',
  'HU',
  'IE',
  'IL',
  'JP',
  'LT',
  'LU',
  'LV',
  'MT',
  'MY',
  'NG',
  'NI',
  'NZ',
  'PA',
  'PL',
  'PT',
  'RO',
  'SA',
  'SG',
  'SV',
  'TW',
  'UA',
  'UY',
  'VE',
  'VN'
]

const data = countries
  .filter((c) => availableCountries.includes(c.code.toUpperCase()))
  .map((c) => ({
    label: c.name,
    value: c.code.toLowerCase(),
    flag: `https://flagcdn.com/16x12/${c.code.toLowerCase()}.png`
  }))
  .sort((a, b) => a.label.localeCompare(b.label))

const SelectItem = forwardRef(({ label, flag, ...others }, ref) => (
  <Box ref={ref} {...others}>
    <Group align="baseline">
      <Image src={flag} alt={label} width={16} height={12} />
      <Text>{label}</Text>
    </Group>
  </Box>
))

SelectItem.displayName = 'SelectItem'
SelectItem.propTypes = {
  label: PropTypes.string.isRequired,
  flag: PropTypes.string.isRequired
}

const CountryInput = ({
  onChange,
  value,
  filterCountries,
  uppercase = false,
  isLoading = false,
  ...other
}) => {
  const { country, setCountry } = useLocaleContext()

  // Mostrar todos los países disponibles
  const filteredData = filterCountries
    ? data.filter((item) => filterCountries.includes(item.value.toUpperCase()))
    : data

  return (
    <Select
      searchable
      icon={
        isLoading ? (
          <Loader size={16} color="rgba(123,57,182,1)" />
        ) : (
          <IconWorld size={20} />
        )
      }
      placeholder="Pick one"
      nothingFound="No options"
      data={filteredData}
      itemComponent={SelectItem}
      value={value || country}
      onChange={onChange || setCountry}
      disabled={isLoading}
      {...other}
      dropdownPosition="bottom"
      withinPortal
      styles={(theme) => ({
        input: {
          fontSize: theme.fontSizes.sm,
          backgroundColor: '#121E274D',
          padding: '4px 8px',
          borderRadius: '10px',
          border: 'none',
          borderBottom: '2px solid #7B39B6',
          opacity: 1,
          position: 'relative',
          ...(uppercase && {
            textTransform: 'uppercase',
            fontWeight: 100,
            fontSize: '16.5px'
          })
        },
        dropdown: {
          zIndex: 1000
        },
        item: {
          fontSize: theme.fontSizes.xs
        }
      })}
    />
  )
}

CountryInput.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string,
  filterCountries: PropTypes.arrayOf(PropTypes.string),
  uppercase: PropTypes.bool,
  isLoading: PropTypes.bool,
  other: PropTypes.object
}

export default CountryInput
